import React, { useState, useEffect, useRef } from 'react';
import { openServiceInterestModal, closeServiceInterestModal } from '../../utils/functions';
import apiService from '../../services/api';

const ServicesSection = () => {
  // Start with no services selected - user selects when opening the form
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    company: '',
    service: []
  });
  const [errors, setErrors] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const nameInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // When modal opens: either pre-fill the service (if user clicked a card) or show no selection. Never restore from storage.
  useEffect(() => {
    const modal = document.getElementById('service-interest-modal');
    if (modal) {
      const observer = new MutationObserver(() => {
        if (modal.style.display === 'flex' || modal.classList.contains('show')) {
          const preselectedService = modal.dataset.preselectedService;
          if (preselectedService) {
            // User opened from a service card: set only this service (no merge, no localStorage)
            setFormData(prev => ({ ...prev, service: [preselectedService] }));
            delete modal.dataset.preselectedService;
          } else {
            // Opened without a card (e.g. from another link): ensure no services selected
            setFormData(prev => (prev.service.length ? { ...prev, service: [] } : prev));
          }
        } else {
          setIsDropdownOpen(false);
        }
      });

      observer.observe(modal, { attributes: true, attributeFilter: ['style', 'class', 'data-preselected-service'] });

      return () => observer.disconnect();
    }
  }, []);
  const services = [
    {
      logo: 'bsb_polymer_logo.webp',
      name: 'BSB | Polymer',
      description: 'A smart marketplace for buyers to explore multiple grades, compare real-time prices, and connect instantly using AI-driven assistance.'
    },
    {
      logo: 'Polymer_Partners.webp',
      name: 'BSB | Polymer Partner',
      description: 'A dedicated supplier platform to list inventory, receive buyer requirements, engage in Polyclub bulk deals, and manage sales efficiently.'
    },
    {
      logo: 'bsb_logistic_logo.webp',
      name: 'BSB | Logistic',
      description: 'A vendor-focused app for transporters to view freight requirements, submit quotes, update fleet availability, and use an integrated freight calculator with instant alerts.'
    },
    {
      logo: 'bsb_finance_logo.webp',
      name: 'BSB | Finance',
      description: 'Enables buyers to access credit facilities through partnered NBFCs, along with a built-in credit finance & interest calculator for clarity and planning.'
    },
    {
      logo: 'bsb_global_sourcing.webp',
      name: 'BSB | Global',
      description: 'Connects buyers with verified global suppliers, supporting sourcing, importing, and end-to-end international polymer trade.'
    },
    {
      logo: 'bsb_custom_logo.webp',
      name: 'BSB | Custom',
      description: 'Provides customs learning support and instant duty calculations through an easy-to-use in-app duty calculator for faster decision-making.'
    },
    {
      logo: 'bsb_Warehouse_logo.webp',
      name: 'BSB | Warehouse',
      description: 'Offers secure and reliable warehouse rental services for safe storage of industrial raw materials.'
    },
    {
      logo: 'bsb_shipping_logo.webp',
      name: 'BSB | Shipping',
      description: 'Provides international freight booking for buyers and suppliers across global routes with transparent and competitive pricing.'
    }
  ];

  // Handle name input - text validation (only letters, spaces, hyphens)
  const handleNameChange = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z\s-]/g, '');
    setFormData({ ...formData, name: value });
    if (errors.name) {
      setErrors({ ...errors, name: '' });
    }
  };

  // Handle mobile input - number validation (only digits, max 10)
  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
    setFormData({ ...formData, mobile: value });
    if (errors.mobile) {
      setErrors({ ...errors, mobile: '' });
    }
  };

  // Handle service checkbox change (no localStorage - user choice only for this session)
  const handleServiceToggle = (serviceName) => {
    setFormData(prev => {
      const isSelected = prev.service.includes(serviceName);
      const newServices = isSelected
        ? prev.service.filter(s => s !== serviceName)
        : [...prev.service, serviceName];
      return { ...prev, service: newServices };
    });

    if (errors.service) {
      setErrors({ ...errors, service: '' });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation - only letters, spaces, hyphens, minimum 2 characters
    if (!formData.name || !formData.name.trim()) {
      newErrors.name = 'Please enter your name';
    } else if (!/^[a-zA-Z\s-]{2,}$/.test(formData.name.trim())) {
      newErrors.name = 'Name should contain only letters, spaces, and hyphens';
    }
    
    // Mobile validation - exactly 10 digits
    if (!formData.mobile || !formData.mobile.trim()) {
      newErrors.mobile = 'Please enter your mobile number';
    } else if (!/^[0-9]{10}$/.test(formData.mobile)) {
      newErrors.mobile = 'Mobile number must be exactly 10 digits';
    }
    
    // Service validation - at least one service selected
    if (!formData.service || formData.service.length === 0) {
      newErrors.service = 'Please select at least one service';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <>
      <section id="services" className="services-section">
        <div className="container">
          <div className="section-header">
            <h2>Our Services</h2>
            <p>Comprehensive solutions for your business needs</p>
          </div>
          
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <div className="service-logo-wrap">
                  <img 
                    src={`/assets/images/logo/${service.logo}`} 
                    alt={service.name} 
                    className="service-logo"
                  />
                </div>
                <p>{service.description}</p>
                <button 
                  className="btn service-interest-btn" 
                  onClick={() => openServiceInterestModal(service.name)}
                >
                  I'm Interested
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div id="service-interest-modal" className="service-modal" aria-hidden="true">
        <div className="service-modal-content">
          <button className="service-modal-close" onClick={() => {
            closeServiceInterestModal();
          }} aria-label="Close service interest form">&times;</button>
          <h3>I'm Interested</h3>
          <p className="service-modal-subtitle">Share your details and our team will connect with you shortly.</p>
          <form id="service-interest-form" onSubmit={async (e) => {
            e.preventDefault();
            
            // Validate form
            if (!validateForm()) {
              return;
            }
            
            // Create FormData for API submission
            const submitFormData = new FormData();
            submitFormData.append('name', formData.name.trim());
            submitFormData.append('mobile', formData.mobile);
            submitFormData.append('company', formData.company.trim());
            // For multi-select, send as comma-separated string or array
            formData.service.forEach(service => {
              submitFormData.append('service[]', service);
            });
            
            try {
              const result = await apiService.submitServiceInterest(submitFormData);
              if (result.success) {
                alert('Thank you for your interest! We will contact you shortly.');
                // Reset form
                setFormData({
                  name: '',
                  mobile: '',
                  company: '',
                  service: []
                });
                setErrors({});
                setIsDropdownOpen(false);
                // Close modal using proper function
                closeServiceInterestModal();
              } else {
                alert(result.message || 'Error submitting form. Please try again.');
              }
            } catch (error) {
              console.error('Error submitting service interest:', error);
              alert('Error submitting form. Please try again.');
            }
          }}>
            <div className="service-form-field">
              <label htmlFor="service-interest-name">Full Name *</label>
              <input 
                type="text" 
                id="service-interest-name" 
                name="name" 
                placeholder="Enter your full name" 
                value={formData.name}
                onChange={handleNameChange}
                ref={nameInputRef}
                required 
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>
            <div className="service-form-field">
              <label htmlFor="service-interest-mobile">Mobile Number *</label>
              <input 
                type="tel" 
                id="service-interest-mobile" 
                name="mobile" 
                placeholder="Enter 10-digit mobile number" 
                maxLength={10}
                inputMode="numeric"
                pattern="[0-9]{10}"
                value={formData.mobile}
                onChange={handleMobileChange}
                ref={mobileInputRef}
                required 
              />
              {errors.mobile && <span className="error-message">{errors.mobile}</span>}
            </div>
            <div className="service-form-field">
              <label htmlFor="service-interest-company">Company Name</label>
              <input 
                type="text" 
                id="service-interest-company" 
                name="company" 
                placeholder="Enter your company name" 
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              />
            </div>
            <div className="service-form-field">
              <label htmlFor="service-interest-type">Service</label>
              <div className="custom-multi-select" ref={dropdownRef}>
                <button
                  type="button"
                  className={`custom-select-trigger ${isDropdownOpen ? 'open' : ''} ${errors.service ? 'error' : ''} ${formData.service.length > 0 ? 'has-selection' : ''}`}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  aria-haspopup="listbox"
                  aria-expanded={isDropdownOpen}
                >
                  <span className="select-placeholder">
                    {formData.service.length > 0 
                      ? `${formData.service.length} service${formData.service.length > 1 ? 's' : ''} selected`
                      : 'Select services'
                    }
                  </span>
                  <span className="select-arrow">
                    <i className={`fas fa-chevron-${isDropdownOpen ? 'up' : 'down'}`}></i>
                  </span>
                </button>
                {isDropdownOpen && (
                  <div className="custom-select-dropdown">
                    <div className="dropdown-list">
                      {services.map((service, index) => (
                        <label key={index} className="dropdown-option">
                          <input
                            type="checkbox"
                            checked={formData.service.includes(service.name)}
                            onChange={() => handleServiceToggle(service.name)}
                          />
                          <span className="checkbox-label">{service.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {errors.service && <span className="error-message">{errors.service}</span>}
              {formData.service.length > 0 && (
                <div className="selected-services">
                  <strong>Selected: </strong>
                  {formData.service.join(', ')}
                </div>
              )}
            </div>
            <button type="submit" className="btn service-submit-btn">Submit Details</button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ServicesSection;

